'use client'

import Image from 'next/image'
import dynamic from 'next/dynamic'
import {
  useState,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
  useMemo,
} from 'react'
import type { ListingMediaItem, ResizedMediaVariant } from '@/lib/feedbridge/types'
import { isPreSizedImage } from '@/lib/feedbridge/helpers'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

const PropertyMapLazy = dynamic(
  () => import('./PropertyMap').then((mod) => ({ default: mod.PropertyMap })),
  { ssr: false, loading: () => <div className="w-full h-[500px] bg-muted animate-pulse" /> },
)

/**
 * Branded image-loading placeholder (Steve 2026-08-23) — was a flat `bg-muted`
 * pulse, which read as a grey hole while the big hero photo downloaded. Now the
 * shared "Loading Ground" already used on listing cards, /guides, /insights and
 * the people grid, so every image on the site fades up off the same ground.
 * The shimmer stops itself under prefers-reduced-motion.
 */
function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`loading-ground loading-ground-shimmer absolute inset-0 ${className}`} />
}

function ImageWithSkeleton({
  src,
  alt,
  sizes,
  priority,
  className = '',
  onClick,
}: {
  src: string
  alt: string
  sizes: string
  priority?: boolean
  className?: string
  onClick?: () => void
}) {
  const [loaded, setLoaded] = useState(false)
  return (
    <>
      {!loaded && <Skeleton />}
      <Image
        src={src}
        alt={alt}
        fill
        className={`${className} transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        sizes={sizes}
        priority={priority}
        // Serve pre-resized CDN variants directly (Steve 2026-08-23 — on-page photos
        // were slow vs the instant gallery): /_next/image was re-decoding and
        // re-encoding an already-WebP, already-resized file at w=3840 on first view,
        // and the resulting URL differed from the ones `preloadAll` warms, so the hero
        // slider could never reuse those preloaded 1920px images. Matching URLs now
        // ⇒ arrow "page turns" hit browser cache, exactly like the lightbox.
        // Full-res originals (no variant available) still go through next/image.
        unoptimized={isPreSizedImage(src)}
        onLoad={() => setLoaded(true)}
        onClick={onClick}
      />
    </>
  )
}

function getUrl(
  item: ListingMediaItem,
  width: number,
  resizedMedia?: Record<string, ResizedMediaVariant[]>,
): string {
  if (!resizedMedia?.[item.id]) return item.url
  const variant =
    resizedMedia[item.id].find((v) => v.width >= width) ||
    resizedMedia[item.id][resizedMedia[item.id].length - 1]
  return variant?.url || item.url
}

function extractYouTubeId(url: string): string | null {
  // Handle youtu.be/ID
  const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/)
  if (shortMatch) return shortMatch[1]
  // Handle youtube.com/watch?v=ID
  const longMatch = url.match(/[?&]v=([a-zA-Z0-9_-]{11})/)
  if (longMatch) return longMatch[1]
  // Handle youtube.com/embed/ID
  const embedMatch = url.match(/embed\/([a-zA-Z0-9_-]{11})/)
  if (embedMatch) return embedMatch[1]
  return null
}

function VideoEmbed({ url }: { url: string }) {
  const [playing, setPlaying] = useState(false)
  const videoId = extractYouTubeId(url)

  if (!videoId) return null

  return (
    <div className="relative w-full aspect-video bg-brand">
      {playing ? (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&showinfo=0`}
          allow="autoplay; fullscreen; encrypted-media"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
          title="Property video tour"
        />
      ) : (
        <button
          onClick={() => setPlaying(true)}
          className="absolute inset-0 w-full h-full group"
          aria-label="Play property video"
        >
          <img
            src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
            alt="Property video thumbnail"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-brand/30 flex items-center justify-center group-hover:bg-brand/40 transition-colors">
            <div className="w-16 h-16 bg-background/90 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 ml-1 text-foreground">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
            </div>
          </div>
        </button>
      )}
    </div>
  )
}

type MediaTab = 'photos' | 'video' | 'floorplan' | 'map'

export interface ImageGalleryHandle {
  openLightbox: (index: number) => void
  /** Open the floorplan lightbox at the first floorplan (no-op without floorplans) */
  openFloorplans: () => void
  /** Open the video lightbox (no-op without a video URL) */
  openVideo: () => void
}

interface ImageGalleryProps {
  images: ListingMediaItem[]
  floorplans: ListingMediaItem[]
  resizedMedia?: Record<string, ResizedMediaVariant[]>
  videoUrl?: string | null
  addressLabel?: string
  mapCoordinates?: { lat: number; lng: number } | null
  /**
   * Render the tabbed Photos / Video / Floorplan / Map browser.
   *
   * The property-detail page sets this false: Figma 4022:84575 has no tabbed or
   * mosaic gallery section — the only gallery is the full-bleed `HeroCarousel`
   * band, and media is otherwise reached from the sidebar `Resources` buttons
   * (4022:88916). The component is still mounted there so the lightbox exposed
   * through `ImageGalleryHandle` stays available.
   */
  showBrowser?: boolean
}

/**
 * Full-bleed property hero carousel — Figma `Frame 506` 4022:93510, the single
 * gallery band that sits directly under the address hero.
 *
 * Geometry: desktop band is 1600 × 760.87 (ratio 1600/761); the hidden neighbour
 * slides 4022:93511 / 4022:93518 share it. Tablet (4022:107263) and mobile
 * (4035:76147) are exactly 4:3.
 *
 * Overlays (all flush to the image edge, gap/gap-0):
 *   · status badge  — theme/primary #000 on white text, badge geometry h22 / px6 / py2
 *   · prev + next   — theme/accent #8cc640 squares vertically centred on the edges
 *   · slide counter — theme/secondary #74cdde "1 OF 23", flush bottom-left
 */
export function HeroCarousel({
  images,
  resizedMedia,
  addressLabel,
  statusLabel,
  onOpen,
}: {
  images: ListingMediaItem[]
  resizedMedia?: Record<string, ResizedMediaVariant[]>
  addressLabel?: string
  /** e.g. "For Sale" / "Sold" — rendered uppercase in the top-left badge */
  statusLabel?: string | null
  onOpen?: () => void
}) {
  const [index, setIndex] = useState(0)
  // Swipe state for the on-page hero (Steve 2026-08-23). Until now only the two
  // lightboxes were swipeable, so on a phone the hero could only be paged by the
  // arrows — every other photo surface on the site swipes, so this one should too.
  const touch = useRef<{ x: number; y: number; swiped: boolean } | null>(null)
  const total = images.length
  const item = images[Math.min(index, Math.max(total - 1, 0))]

  if (!item) return null

  const go = (delta: number) => setIndex((i) => (i + delta + total) % total)

  const SWIPE_MIN_PX = 40

  const onTouchStart = (e: React.TouchEvent) => {
    const point = e.touches[0]
    if (!point) return
    touch.current = { x: point.clientX, y: point.clientY, swiped: false }
  }

  const onTouchMove = (e: React.TouchEvent) => {
    const start = touch.current
    const point = e.touches[0]
    if (!start || !point) return
    const dx = point.clientX - start.x
    const dy = point.clientY - start.y
    if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) start.swiped = true
  }

  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touch.current
    const point = e.changedTouches[0]
    if (!start || !point || total < 2) return
    const dx = point.clientX - start.x
    const dy = point.clientY - start.y
    if (Math.abs(dx) >= SWIPE_MIN_PX && Math.abs(dx) > Math.abs(dy)) {
      go(dx < 0 ? 1 : -1)
      start.swiped = true
    }
  }

  // The hero image opens the lightbox on click — a swipe must not also open it.
  const onClickCapture = (e: React.MouseEvent) => {
    if (touch.current?.swiped) {
      e.preventDefault()
      e.stopPropagation()
      touch.current.swiped = false
    }
  }

  return (
    <div
      className="relative w-full overflow-hidden bg-tertiary aspect-[4/3] lg:aspect-[1600/761]"
      data-visual="property-hero-gallery"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onClickCapture={onClickCapture}
      // Vertical drags stay with the browser so the page still scrolls.
      style={{ touchAction: 'pan-y' }}
    >
      <ImageWithSkeleton
        key={item.id}
        src={getUrl(item, 1920, resizedMedia)}
        alt={
          addressLabel
            ? `Property photo ${index + 1} — ${addressLabel}`
            : `Property photo ${index + 1}`
        }
        sizes="100vw"
        priority={index === 0}
        className={`object-cover ${onOpen ? 'cursor-pointer' : ''}`}
        onClick={onOpen}
      />

      {statusLabel && (
        <span className="absolute top-0 left-0 z-[2] inline-flex items-center justify-center h-[22px] px-1.5 py-0.5 bg-brand text-brand-foreground text-body-xs font-normal uppercase whitespace-nowrap">
          {statusLabel}
        </span>
      )}

      {total > 1 && (
        <>
          {/* Canonical `icon button` 4022:93514/93515 — 36×36 (size=icon),
              theme/accent fill, radius sm, 16px Lucide chevron, inset gap-2 8px
              from the image edge and vertically centred (arrow row 4022:93513 is
              justify-between px-2 in a justify-between column). */}
          <Button
            type="button"
            size="icon"
            onClick={() => go(-1)}
            aria-label="Previous photo"
            className="absolute left-2 top-1/2 z-[2] -translate-y-1/2"
          >
            <svg
              className="size-4"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
          </Button>
          <Button
            type="button"
            size="icon"
            onClick={() => go(1)}
            aria-label="Next photo"
            className="absolute right-2 top-1/2 z-[2] -translate-y-1/2"
          >
            <svg
              className="size-4"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </Button>
        </>
      )}

      <span className="absolute bottom-0 left-0 z-[2] inline-flex items-center justify-center h-[22px] px-1.5 py-0.5 bg-secondary text-secondary-foreground text-body-xs font-light uppercase whitespace-nowrap">
        {index + 1} of {total}
      </span>
    </div>
  )
}

export const ImageGallery = forwardRef<ImageGalleryHandle, ImageGalleryProps>(function ImageGallery(
  { images, floorplans, resizedMedia, videoUrl, addressLabel, mapCoordinates, showBrowser = true },
  ref,
) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [floorplanLightboxIndex, setFloorplanLightboxIndex] = useState<number | null>(null)
  const allMedia = useMemo(() => [...images, ...floorplans], [images, floorplans])

  // Drag-to-swipe state
  const dragRef = useRef<{
    startX: number
    startY: number
    currentX: number
    committed: boolean
  } | null>(null)
  const [dragOffset, setDragOffset] = useState(0)
  const [isSnapping, setIsSnapping] = useState(false)

  // Lightbox image loading state
  const [lightboxImageLoaded, setLightboxImageLoaded] = useState(false)
  const activeIndex = lightboxIndex ?? floorplanLightboxIndex
  useEffect(() => {
    setLightboxImageLoaded(false)
  }, [activeIndex])

  // Preload all gallery images at lightbox resolution, once. The lightbox
  // <Image>s are `unoptimized` so they request these exact URLs — preloading a
  // URL the lightbox never asks for is a guaranteed cache miss and a double
  // download (JR gallery fix, webmate 898bce3). Fired in parallel: HTTP/2
  // multiplexes and the browser prioritises the visible image.
  const preloadedRef = useRef(false)
  const preloadAll = useCallback(() => {
    if (preloadedRef.current) return
    preloadedRef.current = true
    const urls = [
      ...allMedia.map((item) => getUrl(item, 1920, resizedMedia)),
      ...floorplans.map((fp) => fp.url),
    ]
    urls.forEach((url) => {
      const img = new window.Image()
      img.src = url
    })
  }, [allMedia, floorplans, resizedMedia])

  // Preload as soon as a lightbox opens — may beat the idle callback (webmate 71c2ab6)
  useEffect(() => {
    if (activeIndex !== null) preloadAll()
  }, [activeIndex, preloadAll])

  // Otherwise wait for full page load + idle so the preload never competes
  // with the page's own resources
  useEffect(() => {
    // Wait until the page is fully loaded before starting
    if (document.readyState === 'complete') {
      // Use requestIdleCallback if available, otherwise a short delay
      if ('requestIdleCallback' in window) {
        const id = requestIdleCallback(preloadAll)
        return () => cancelIdleCallback(id)
      } else {
        const id = setTimeout(preloadAll, 1000)
        return () => clearTimeout(id)
      }
    } else {
      const onLoad = () => {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(preloadAll)
        } else {
          setTimeout(preloadAll, 1000)
        }
      }
      window.addEventListener('load', onLoad)
      return () => window.removeEventListener('load', onLoad)
    }
  }, [preloadAll])

  // Shared touch handlers for swipeable lightboxes
  const makeTouchHandlers = (
    index: number,
    total: number,
    setIndex: React.Dispatch<React.SetStateAction<number | null>>,
  ) => ({
    onTouchStart: (e: React.TouchEvent) => {
      setIsSnapping(false)
      dragRef.current = {
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        currentX: e.touches[0].clientX,
        committed: false,
      }
    },
    onTouchMove: (e: React.TouchEvent) => {
      if (!dragRef.current) return
      const x = e.touches[0].clientX
      const deltaX = x - dragRef.current.startX
      const deltaY = e.touches[0].clientY - dragRef.current.startY

      // If not yet committed to a direction, check if this is vertical scroll
      if (!dragRef.current.committed) {
        if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 10) {
          dragRef.current = null
          setDragOffset(0)
          return
        }
        if (Math.abs(deltaX) > 10) {
          dragRef.current.committed = true
        }
      }

      if (dragRef.current.committed) {
        // Resist dragging past boundaries
        let clamped = deltaX
        if ((index === 0 && deltaX > 0) || (index === total - 1 && deltaX < 0)) {
          clamped = deltaX * 0.3
        }
        dragRef.current.currentX = x
        setDragOffset(clamped)
      }
    },
    onTouchEnd: () => {
      if (!dragRef.current) return
      const deltaX = dragRef.current.currentX - dragRef.current.startX
      const threshold = 50
      dragRef.current = null

      if (Math.abs(deltaX) > threshold) {
        if (deltaX < 0 && index < total - 1) {
          setIsSnapping(true)
          setDragOffset(0)
          setIndex((prev) => (prev !== null ? prev + 1 : prev))
          setTimeout(() => setIsSnapping(false), 200)
          return
        }
        if (deltaX > 0 && index > 0) {
          setIsSnapping(true)
          setDragOffset(0)
          setIndex((prev) => (prev !== null ? prev - 1 : prev))
          setTimeout(() => setIsSnapping(false), 200)
          return
        }
      }
      // Snap back
      setIsSnapping(true)
      setDragOffset(0)
      setTimeout(() => setIsSnapping(false), 200)
    },
  })

  const hasVideo = Boolean(videoUrl)
  const hasFloorplans = floorplans.length > 0
  const hasMap = Boolean(mapCoordinates)
  const hasMultipleTabs =
    [images.length > 0, hasVideo, hasFloorplans, hasMap].filter(Boolean).length > 1

  const [activeTab, setActiveTab] = useState<MediaTab>('photos')

  const [videoLightboxOpen, setVideoLightboxOpen] = useState(false)

  useImperativeHandle(ref, () => ({
    openLightbox: (index: number) => setLightboxIndex(index),
    openFloorplans: () => {
      if (floorplans.length > 0) setFloorplanLightboxIndex(0)
    },
    openVideo: () => {
      if (videoUrl) setVideoLightboxOpen(true)
    },
  }))

  const closeLightbox = useCallback(() => setLightboxIndex(null), [])
  const closeFloorplanLightbox = useCallback(() => setFloorplanLightboxIndex(null), [])
  const closeVideoLightbox = useCallback(() => setVideoLightboxOpen(false), [])

  useEffect(() => {
    if (!videoLightboxOpen) return
    document.body.style.overflow = 'hidden'
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeVideoLightbox()
    }
    document.addEventListener('keydown', handleKey)
    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handleKey)
    }
  }, [videoLightboxOpen, closeVideoLightbox])

  useEffect(() => {
    if (lightboxIndex === null) return
    document.body.style.overflow = 'hidden'
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox()
      if (e.key === 'ArrowRight')
        setLightboxIndex((prev) => (prev !== null && prev < allMedia.length - 1 ? prev + 1 : prev))
      if (e.key === 'ArrowLeft')
        setLightboxIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : prev))
    }
    document.addEventListener('keydown', handleKey)
    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handleKey)
    }
  }, [lightboxIndex, allMedia.length, closeLightbox])

  useEffect(() => {
    if (floorplanLightboxIndex === null) return
    document.body.style.overflow = 'hidden'
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeFloorplanLightbox()
      if (e.key === 'ArrowRight')
        setFloorplanLightboxIndex((prev) =>
          prev !== null && prev < floorplans.length - 1 ? prev + 1 : prev,
        )
      if (e.key === 'ArrowLeft')
        setFloorplanLightboxIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : prev))
    }
    document.addEventListener('keydown', handleKey)
    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handleKey)
    }
  }, [floorplanLightboxIndex, floorplans.length, closeFloorplanLightbox])

  if (!images.length && !hasVideo) return null

  const gridImages = images.slice(0, 5)
  const remaining = images.length - 5

  const tabs: { id: MediaTab; label: string }[] = [
    ...(images.length > 0 ? [{ id: 'photos' as const, label: `Photos (${images.length})` }] : []),
    ...(hasVideo ? [{ id: 'video' as const, label: 'Video' }] : []),
    ...(hasFloorplans
      ? [{ id: 'floorplan' as const, label: `Floorplan${floorplans.length > 1 ? 's' : ''}` }]
      : []),
    ...(hasMap ? [{ id: 'map' as const, label: 'Map' }] : []),
  ]

  // Panels are lifted out so the same markup serves both the tabbed browser and the
  // single-media fallback (which renders no tablist, exactly as before).
  const photosPanel = images.length > 0 && (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-1 md:h-[500px]">
      {/* Large image on left — 2 cols */}
      <div
        className="group md:col-span-2 relative cursor-pointer overflow-hidden aspect-[4/3] md:aspect-auto"
        onClick={() => setLightboxIndex(0)}
      >
        <ImageWithSkeleton
          src={getUrl(gridImages[0], 1200, resizedMedia)}
          alt={addressLabel ? `Property photo 1 — ${addressLabel}` : 'Property photo 1'}
          sizes="(max-width: 768px) 100vw, 66vw"
          className="object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>

      {/* Smaller images on right in 2x2 grid */}
      <div className="hidden md:grid md:col-span-1 grid-cols-2 grid-rows-2 gap-1 h-[500px]">
        {gridImages.slice(1, 5).map((item, i) => (
          <div
            key={item.id}
            className="group relative cursor-pointer overflow-hidden"
            onClick={() => setLightboxIndex(i + 1)}
          >
            <ImageWithSkeleton
              src={getUrl(item, 800, resizedMedia)}
              alt={
                addressLabel
                  ? `Property photo ${i + 2} — ${addressLabel}`
                  : `Property photo ${i + 2}`
              }
              sizes="25vw"
              className="object-cover group-hover:scale-105 transition-transform duration-500"
            />
            {i === 3 && remaining > 0 && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <span className="text-white text-display-xl font-semibold">+{remaining}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )

  const videoPanel = videoUrl ? <VideoEmbed url={videoUrl} /> : null

  const floorplanPanel = hasFloorplans ? (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {floorplans.map((fp, i) => (
        <div
          key={fp.id}
          className="relative aspect-[4/3] bg-muted cursor-pointer group"
          onClick={() => setFloorplanLightboxIndex(i)}
        >
          <Image
            src={fp.url}
            alt={
              addressLabel
                ? `Floorplan${floorplans.length > 1 ? ` ${i + 1}` : ''} — ${addressLabel}`
                : `Floorplan ${i + 1}`
            }
            fill
            className="object-contain group-hover:scale-105 transition-transform duration-500"
            sizes="(max-width: 768px) 100vw, 50vw"
          />
        </div>
      ))}
    </div>
  ) : null

  const mapPanel =
    hasMap && mapCoordinates ? (
      <PropertyMapLazy lat={mapCoordinates.lat} lng={mapCoordinates.lng} />
    ) : null

  return (
    <>
      {/* Media browser. The strip was a bespoke underlined tab row (hardcoded 14px and
          #000000 tints) with no roving tabindex and no tab/tabpanel semantics; it now
          uses the canonical Tabs (src/components/ui/tabs.tsx), which carries the Figma
          tab spec — theme/muted track, theme/accent active fill, Body/bodySmAlt 14/16,
          radius sm. NB: Figma's property-detail frames (4022:84575 and siblings) have no
          tabbed gallery at all — the property page mounts this with showBrowser={false}. */}
      {showBrowser &&
        (hasMultipleTabs ? (
          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as MediaTab)}
            className="flex flex-col gap-4"
          >
            <TabsList aria-label="Property media">
              {tabs.map((tab) => (
                <TabsTrigger key={tab.id} value={tab.id}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {images.length > 0 && <TabsContent value="photos">{photosPanel}</TabsContent>}
            {hasVideo && <TabsContent value="video">{videoPanel}</TabsContent>}
            {hasFloorplans && <TabsContent value="floorplan">{floorplanPanel}</TabsContent>}
            {hasMap && <TabsContent value="map">{mapPanel}</TabsContent>}
          </Tabs>
        ) : (
          activeTab === 'photos' && photosPanel
        ))}

      {/* Video lightbox — same dark chrome as the photo/floorplan lightboxes */}
      {videoLightboxOpen && videoUrl && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col" onClick={closeVideoLightbox}>
          <div className="flex items-center justify-between px-4 py-2 md:px-6 md:py-4 shrink-0">
            <span className="text-white/70 text-body-sm">Video</span>
            <button
              onClick={closeVideoLightbox}
              className="text-white/70 hover:text-white transition-colors p-2"
              aria-label="Close"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center min-h-0 px-4 md:px-16 pb-6">
            <div className="w-full max-w-6xl" onClick={(e) => e.stopPropagation()}>
              <VideoEmbed url={videoUrl} />
            </div>
          </div>
        </div>
      )}

      {/* Floorplan lightbox */}
      {floorplanLightboxIndex !== null &&
        (() => {
          const touchHandlers = makeTouchHandlers(
            floorplanLightboxIndex,
            floorplans.length,
            setFloorplanLightboxIndex,
          )
          return (
            <div
              className="fixed inset-0 z-50 bg-black flex flex-col"
              onClick={closeFloorplanLightbox}
              {...touchHandlers}
            >
              {/* Loading progress bar */}
              {!lightboxImageLoaded && (
                <div className="absolute top-0 left-0 right-0 h-[2px] z-20 overflow-hidden">
                  <div
                    className="h-full bg-white/50"
                    style={{ width: '70%', animation: 'lightbox-progress 800ms ease-out forwards' }}
                  />
                </div>
              )}

              <div className="flex items-center justify-between px-4 py-2 md:px-6 md:py-4 shrink-0">
                <span className="text-white/70 text-body-sm">
                  {floorplans.length > 1
                    ? `Floorplan ${floorplanLightboxIndex + 1} / ${floorplans.length}`
                    : 'Floorplan'}
                </span>
                <button
                  onClick={closeFloorplanLightbox}
                  className="text-white/70 hover:text-white transition-colors p-2"
                  aria-label="Close"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 flex items-center justify-center min-h-0 px-0 md:px-16 relative">
                <div
                  key={floorplanLightboxIndex}
                  className="relative w-full h-full max-w-6xl max-h-[calc(100vh-56px)] md:max-h-[calc(100vh-120px)]"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    transform: `translateX(${dragOffset}px)`,
                    transition: isSnapping ? 'transform 200ms ease-out' : 'none',
                  }}
                >
                  <Image
                    src={floorplans[floorplanLightboxIndex].url}
                    alt={
                      addressLabel
                        ? `Floorplan ${floorplanLightboxIndex + 1} — ${addressLabel}`
                        : `Floorplan ${floorplanLightboxIndex + 1}`
                    }
                    fill
                    className={`object-contain transition-opacity duration-300 ${lightboxImageLoaded ? 'opacity-100' : 'opacity-0'}`}
                    sizes="100vw"
                    priority
                    unoptimized
                    onLoad={() => setLightboxImageLoaded(true)}
                  />
                  {/* Nav arrows overlaid on image */}
                  {floorplans.length > 1 && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setFloorplanLightboxIndex((prev) =>
                            prev !== null && prev > 0 ? prev - 1 : prev,
                          )
                        }}
                        className="absolute left-0 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors z-10 disabled:opacity-20 p-3 bg-black/20 hover:bg-black/40"
                        disabled={floorplanLightboxIndex === 0}
                        aria-label="Previous floorplan"
                      >
                        <svg
                          width="28"
                          height="28"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="15 18 9 12 15 6" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setFloorplanLightboxIndex((prev) =>
                            prev !== null && prev < floorplans.length - 1 ? prev + 1 : prev,
                          )
                        }}
                        className="absolute right-0 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors z-10 disabled:opacity-20 p-3 bg-black/20 hover:bg-black/40"
                        disabled={floorplanLightboxIndex === floorplans.length - 1}
                        aria-label="Next floorplan"
                      >
                        <svg
                          width="28"
                          height="28"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )
        })()}

      {/* Lightbox */}
      {lightboxIndex !== null &&
        (() => {
          const touchHandlers = makeTouchHandlers(lightboxIndex, allMedia.length, setLightboxIndex)
          return (
            <div className="fixed inset-0 z-50 bg-black flex flex-col" {...touchHandlers}>
              {/* Loading progress bar */}
              {!lightboxImageLoaded && (
                <div className="absolute top-0 left-0 right-0 h-[2px] z-20 overflow-hidden">
                  <div
                    className="h-full bg-white/50"
                    style={{ width: '70%', animation: 'lightbox-progress 800ms ease-out forwards' }}
                  />
                </div>
              )}

              {/* Top bar */}
              <div className="flex items-center justify-between px-4 py-2 md:px-6 md:py-4 shrink-0">
                <span className="text-white/70 text-body-sm">
                  {lightboxIndex + 1} / {allMedia.length}
                </span>
                <button
                  onClick={closeLightbox}
                  className="text-white/70 hover:text-white transition-colors p-2"
                  aria-label="Close"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              {/* Image area */}
              <div className="flex-1 flex items-center justify-center min-h-0 px-0 md:px-16 relative">
                <div
                  key={lightboxIndex}
                  className="relative w-full h-full max-w-6xl max-h-[calc(100vh-56px)] md:max-h-[calc(100vh-120px)]"
                  style={{
                    transform: `translateX(${dragOffset}px)`,
                    transition: isSnapping ? 'transform 200ms ease-out' : 'none',
                  }}
                >
                  <Image
                    src={getUrl(allMedia[lightboxIndex], 1920, resizedMedia)}
                    alt={
                      addressLabel
                        ? `Property photo ${lightboxIndex + 1} — ${addressLabel}`
                        : `Property photo ${lightboxIndex + 1}`
                    }
                    fill
                    className={`object-contain transition-opacity duration-300 ${lightboxImageLoaded ? 'opacity-100' : 'opacity-0'}`}
                    sizes="100vw"
                    priority
                    unoptimized
                    onLoad={() => setLightboxImageLoaded(true)}
                  />
                </div>
                {/* Nav arrows — outside image container for reliable click targets */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setLightboxIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : prev))
                  }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 text-white/70 hover:text-white active:text-white transition-colors z-10 disabled:opacity-20 p-4 md:p-3 bg-black/20 hover:bg-black/40"
                  disabled={lightboxIndex === 0}
                  aria-label="Previous image"
                >
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setLightboxIndex((prev) =>
                      prev !== null && prev < allMedia.length - 1 ? prev + 1 : prev,
                    )
                  }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-white/70 hover:text-white active:text-white transition-colors z-10 disabled:opacity-20 p-4 md:p-3 bg-black/20 hover:bg-black/40"
                  disabled={lightboxIndex === allMedia.length - 1}
                  aria-label="Next image"
                >
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            </div>
          )
        })()}
    </>
  )
})
